// Contrato de API real (PR-06, ver docs/novas/doc_back/PR-06-renegociation-multiple-sinisters-frontend.md).
//
// Decisão de produto: não existe uma tela separada de "Negociações" — negociação É o acordo, então
// esse contexto (sinistros vinculados, histórico de renegociação, saldo) aparece dentro do próprio
// detalhe do Acordo (ver src/pages/agreements/components/AgreementNegotiationSection.tsx), não num
// menu/rota próprios.
//
// GET  /negotiations?_page=&_limit=
// GET  /negotiations/:id
// GET  /negotiations/:id/history
// GET  /negotiations/:id/balance
// POST /negotiations                        { sinisterIds, status? }
// POST /negotiations/:id/add-sinister        { sinisterId }
// POST /negotiations/:id/remove-sinister     { sinisterId }
// POST /negotiations/:id/renegotiate         { newValueIds, discountPercent?, paymentMethod, installments, dueDate, reason? }
// POST /agreements/:id/cancel                { reason }
//
// O modelo do PR-06 agrupa múltiplos sinistros numa "negociação" e encadeia acordos por
// `previousAgreementId` — algo que não existe em nenhum lugar do que já temos (nosso IAgreement é
// sempre 1 sinistro : 1 acordo, sem `negotiationId`). `findOrCreateNegotiation` é uma ponte
// client-side pra conectar os dois mundos: procura uma negociação existente pra aquele sinistro e,
// se não achar, cria uma na hora. Isso é um workaround temporário — o ideal é o backend expor
// diretamente um `negotiationId` no acordo/sinistro pra eliminar essa busca.
import { AxiosError } from "axios";
import { Api } from "../axios-config";
import { extractApiErrorMessage } from "../../../utils/extractApiErrorMessage";
import { NegotiationPaymentMethod } from "./SinisterNegotiationService";
import { SinisterAgreementService, IAgreement, AgreementStatus } from "./SinisterAgreementService";

export type NegotiationStatus = "OPEN" | "CLOSED";
export type NegotiationAgreementStatus = "ACTIVE" | "RENEGOTIATED" | "CANCELED" | "FINISHED";

export interface INegotiationAgreementSummary {
	id: string;
	status: NegotiationAgreementStatus;
	previousAgreementId: string | null;
	totalValue: number;
	installmentsCount: number;
	createdAt: string;
}

export interface INegotiation {
	id: string;
	status: NegotiationStatus;
	clientId: string;
	activeAgreementId: string | null;
	createdAt: string;
	closedAt: string | null;
	sinisterIds: string[];
	agreements?: INegotiationAgreementSummary[];
	agreementsCount?: number;
}

export interface INegotiationBalance {
	negotiationId: string;
	activeAgreementId: string;
	paidAmount: number;
	openAmount: number;
	overdueAmount: number;
	totalValue: number;
}

export type INegotiationHistoryEntry = INegotiationAgreementSummary;

export interface IRenegotiatePayload {
	newValueIds: string[];
	discountPercent?: number;
	paymentMethod: NegotiationPaymentMethod;
	installments: number;
	dueDate: string;
	reason?: string;
	// Ainda não fazem parte do contrato documentado do PR-06 — mandados otimista, mesmo padrão já
	// usado pro downPaymentValue original do acordo (ver SinisterAgreementService.ts).
	downPaymentValue?: number;
	downPaymentDate?: string;
}

const getNegotiations = async (page = 1, limit = 50): Promise<{ data: INegotiation[]; total: number } | Error> => {
	try {
		const { data } = await Api().get(`/negotiations?_page=${page}&_limit=${limit}`);
		return { data: data?.data ?? [], total: data?.total ?? 0 };
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao listar negociações.");
	}
};

const createNegotiation = async (sinisterIds: string[]): Promise<INegotiation | Error> => {
	try {
		const { data } = await Api().post(`/negotiations`, { sinisterIds, status: "OPEN" });
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao criar negociação.");
	}
};

const findNegotiationBySinisterId = async (sinisterId: string): Promise<INegotiation | null | Error> => {
	const list = await getNegotiations(1, 100);
	if (list instanceof Error) return list;

	return list.data.find((negotiation) => negotiation.sinisterIds?.includes(sinisterId)) ?? null;
};

const findOrCreateNegotiation = async (sinisterId: string): Promise<INegotiation | Error> => {
	const existing = await findNegotiationBySinisterId(sinisterId);
	if (existing instanceof Error) return existing;
	if (existing) return existing;

	return createNegotiation([sinisterId]);
};

const getNegotiationById = async (id: string): Promise<INegotiation | Error> => {
	try {
		const { data } = await Api().get(`/negotiations/${id}`);
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao buscar negociação.");
	}
};

const getNegotiationHistory = async (id: string): Promise<INegotiationHistoryEntry[] | Error> => {
	try {
		const { data } = await Api().get(`/negotiations/${id}/history`);
		return data ?? [];
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao buscar histórico da negociação.");
	}
};

const getNegotiationBalance = async (id: string): Promise<INegotiationBalance | Error> => {
	try {
		const { data } = await Api().get(`/negotiations/${id}/balance`);
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao buscar saldo da negociação.");
	}
};

const addSinisterToNegotiation = async (id: string, sinisterId: string): Promise<INegotiation | Error> => {
	try {
		const { data } = await Api().post(`/negotiations/${id}/add-sinister`, { sinisterId });
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao adicionar sinistro na negociação.");
	}
};

const removeSinisterFromNegotiation = async (id: string, sinisterId: string): Promise<INegotiation | Error> => {
	try {
		const { data } = await Api().post(`/negotiations/${id}/remove-sinister`, { sinisterId });
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao remover sinistro da negociação.");
	}
};

const renegotiate = async (negotiationId: string, payload: IRenegotiatePayload): Promise<INegotiation | Error> => {
	try {
		const { data } = await Api().post(`/negotiations/${negotiationId}/renegotiate`, payload);
		return data;
	} catch (error) {
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao renegociar o acordo.");
	}
};

const renegotiateBySinisterId = async (sinisterId: string, payload: IRenegotiatePayload): Promise<INegotiation | Error> => {
	const negotiation = await findOrCreateNegotiation(sinisterId);
	if (negotiation instanceof Error) return negotiation;
	return renegotiate(negotiation.id, payload);
};

const cancelAgreement = async (agreementId: string, reason: string): Promise<IAgreement | Error> => {
	try {
		await Api().post(`/agreements/${agreementId}/cancel`, { reason });
		return SinisterAgreementService.getById(agreementId);
	} catch (error) {
		if (error instanceof AxiosError) {
			// Endpoint dedicado ainda não disponível/aceito — cai no mesmo fallback local do PATCH de status.
			const fallbackStatus: AgreementStatus = "broken";
			return SinisterAgreementService.updateStatus(agreementId, { status: fallbackStatus, reason });
		}
		console.error(error);
		return extractApiErrorMessage(error, "Erro ao cancelar o acordo.");
	}
};

export const AgreementRenegotiationService = {
	getNegotiations,
	createNegotiation,
	findNegotiationBySinisterId,
	findOrCreateNegotiation,
	getNegotiationById,
	getNegotiationHistory,
	getNegotiationBalance,
	addSinisterToNegotiation,
	removeSinisterFromNegotiation,
	renegotiate,
	renegotiateBySinisterId,
	cancelAgreement,
};
